import {useEffect, useCallback} from 'react'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getSiteEnvVarValue} from '../utils/site-config'
import {useBrand} from '../contexts/brand-context'

export function GTMScript() {
    const config = getConfig()
    const {site} = useMultiSite()
    const siteId = site?.id
    const {isGarage} = useBrand()
    const GTM_ENABLED = getSiteEnvVarValue(config.app, 'GTM_ENABLED', siteId)
    const GARAGE_GTM_CONTAINER_ID = getSiteEnvVarValue(config.app, 'GARAGE_GTM_CONTAINER_ID', siteId)
    const GARAGE_GTM_SCRIPT_SOURCE = getSiteEnvVarValue(config.app, 'GARAGE_GTM_SCRIPT_SOURCE', siteId)
    const GARAGE_GTM_NO_SCRIPT_SOURCE = getSiteEnvVarValue(config.app, 'GARAGE_GTM_NO_SCRIPT_SOURCE', siteId)
    const DYNAMITE_GTM_CONTAINER_ID = getSiteEnvVarValue(config.app, 'DYNAMITE_GTM_CONTAINER_ID', siteId)
    const DYNAMITE_GTM_SCRIPT_SOURCE = getSiteEnvVarValue(config.app, 'DYNAMITE_GTM_SCRIPT_SOURCE', siteId)
    const DYNAMITE_GTM_NO_SCRIPT_SOURCE = getSiteEnvVarValue(config.app, 'DYNAMITE_GTM_NO_SCRIPT_SOURCE', siteId)

    // Load GTM after the `load` event (post-LCP), then on idle, so its tag cascade
    // never competes with the hero image paint.
    const deferScriptLoading = useCallback((callback) => {
        if (typeof window === 'undefined') return

        const runWhenIdle = () =>
            'requestIdleCallback' in window
                ? window.requestIdleCallback(callback, {timeout: 3000})
                : setTimeout(callback, 1)

        if (document.readyState === 'complete') {
            runWhenIdle()
        } else {
            window.addEventListener('load', runWhenIdle, {once: true})
        }
    }, [])

    useEffect(() => {
        const loadGTM = () => {
            const cspNonceData = window.__CSP_NONCE__
            // Check if GTM is disabled or script is already loaded
            // Note: env vars are strings, so 'false' is truthy - must compare to 'true'
            if (GTM_ENABLED !== 'true' || document.getElementById('gtm-script')) {
                return
            }

            const containerId = isGarage ? GARAGE_GTM_CONTAINER_ID : DYNAMITE_GTM_CONTAINER_ID
            const scriptSource = isGarage ? GARAGE_GTM_SCRIPT_SOURCE : DYNAMITE_GTM_SCRIPT_SOURCE
            const noScriptSource = isGarage ? GARAGE_GTM_NO_SCRIPT_SOURCE : DYNAMITE_GTM_NO_SCRIPT_SOURCE

            if (containerId && scriptSource && noScriptSource) {
                // Create a script element for the dataLayer
                const dataLayerScript = document.createElement('script')
                dataLayerScript.text = 'window.dataLayer = window.dataLayer || [];'
                dataLayerScript.nonce = cspNonceData
                document.head.appendChild(dataLayerScript)

                // Create a script element for the GTM
                const gtmScript = document.createElement('script')
                gtmScript.id = 'gtm-script'
                gtmScript.nonce = cspNonceData
                gtmScript.text = `
                    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=${scriptSource};
                    var n=d.querySelector('[nonce]');n&&j.setAttribute('nonce',n.nonce||n.getAttribute('nonce'));
                    f.parentNode.insertBefore(j,f);
                    })(window,document,'script','dataLayer','${containerId}');
                `
                document.head.appendChild(gtmScript)

                // Create a script element for the GTM NoScript
                const gtmNoScript = document.createElement('noscript')
                gtmNoScript.id = 'gtm-noscript'
                gtmNoScript.innerHTML = `
                    <iframe src="${noScriptSource}"
                    height="0" width="0" style="display:none;visibility:hidden"></iframe>
                `
                document.body.appendChild(gtmNoScript)
            }
        }

        // Defer GTM loading until browser is idle (after React hydration)
        deferScriptLoading(loadGTM)
    }, [deferScriptLoading])

    return null
}
